import "./login.scss";
import React, { useEffect, useState } from "react";
import { Amplify } from "aws-amplify";
import {
  signIn,
  confirmSignIn,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
} from "aws-amplify/auth";
import config from "../../amplifyconfiguration.json";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PortraitIcon from "@mui/icons-material/Portrait";
import KeyIcon from "@mui/icons-material/Key";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { useNavigate } from "react-router-dom";

Amplify.configure(config);

const MODES = {
  SIGN_IN: "signIn",
  REQUEST_RESET: "requestReset",
  CONFIRM_RESET: "confirmReset",
  RESET_SUCCESS: "resetSuccess",
};

export const Login = () => {
  const navigate = useNavigate();

  const [userName, setUserName] = useState("");
  const [passWord, setPassword] = useState("");
  const [showSetNewPassword, setShowSetNewPassword] = useState(false);
  const [newPasswordString, setNewPasswordString] = useState("");
  const [confirmNewPasswordString, setConfirmNewPasswordString] = useState("");

  const [loginMode, setLoginMode] = useState(MODES.SIGN_IN);
  const [resetCode, setResetCode] = useState("");
  const [resetPasswordValue, setResetPasswordValue] = useState("");
  const [confirmResetPasswordValue, setConfirmResetPasswordValue] = useState("");
  const [resetDeliveryDestination, setResetDeliveryDestination] = useState("");

  const [error, setError] = useState("");
  const [authActionLoading, setAuthActionLoading] = useState(false);
  const [loadingSequence, setLoadingSequence] = useState(false);
  const [currentPhrase, setCurrentPhrase] = useState("");

  const passwordsMatch =
    Boolean(newPasswordString) &&
    newPasswordString === confirmNewPasswordString;

  const resetPasswordsMatch =
    Boolean(resetPasswordValue) &&
    resetPasswordValue === confirmResetPasswordValue;

  const normalizeUsername = () => userName.trim().toLowerCase();

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await getCurrentUser();
        if (user?.username) navigate("/");
      } catch {
        // No active session.
      }
    };

    checkUser();
  }, [navigate]);

  const runLoadingSequence = async () => {
    // const phrases = [
    //   "Establishing Connection...",
    //   "Verifying Credentials...",
    //   "Compiling Intelligence...",
    //   "Access Granted",
    // ];

    // setLoadingSequence(true);

    // for (const phrase of phrases) {
    //   let text = "";
    //   for (const character of phrase) {
    //     text += character;
    //     setCurrentPhrase(text);
    //     await new Promise((resolve) => setTimeout(resolve, 30));
    //   }
    //   await new Promise((resolve) => setTimeout(resolve, 800));
    // }

    // await new Promise((resolve) => setTimeout(resolve, 400));
    navigate("/");
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    setError("");
    setAuthActionLoading(true);

    try {
      if (showSetNewPassword) {
        if (!passwordsMatch) {
          setError("The new passwords do not match.");
          return;
        }

        const result = await confirmSignIn({
          challengeResponse: newPasswordString,
          options: {
            userAttributes: { name: normalizeUsername() },
          },
        });

        if (result.isSignedIn) await runLoadingSequence();
        return;
      }

      const result = await signIn({
        username: normalizeUsername(),
        password: passWord,
      });

      if (
        result.nextStep?.signInStep ===
        "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
      ) {
        setShowSetNewPassword(true);
        toast.info("A new password is required to activate this account.");
        return;
      }

      if (result.isSignedIn) {
        await runLoadingSequence();
      } else {
        setError(
          "Additional verification is required. Contact info@weathervaneintel.com for assistance."
        );
      }
    } catch (authError) {
      console.error("Authentication error:", authError);

      if (authError?.name === "NotAuthorizedException") {
        setError("Incorrect email address or password.");
      } else if (authError?.name === "InvalidPasswordException") {
        setError("The password does not meet the security requirements.");
      } else if (
        authError?.name === "LimitExceededException" ||
        authError?.name === "TooManyRequestsException"
      ) {
        setError("Too many attempts were made. Wait a few minutes and try again.");
      } else {
        setError("We could not complete the request. Please try again.");
      }
    } finally {
      setAuthActionLoading(false);
    }
  };

  const handleRequestReset = async (event) => {
    event.preventDefault();

    if (!normalizeUsername()) {
      setError("Enter the email address associated with your account.");
      return;
    }

    setError("");
    setAuthActionLoading(true);

    try {
      const output = await resetPassword({ username: normalizeUsername() });
      setResetDeliveryDestination(
        output.nextStep?.codeDeliveryDetails?.destination ||
          "the verified email address associated with the account"
      );
    } catch (resetError) {
      console.error("Password-reset request error:", resetError);
      setResetDeliveryDestination(
        "the verified email address associated with the account"
      );
    } finally {
      setLoginMode(MODES.CONFIRM_RESET);
      setAuthActionLoading(false);
    }
  };

  const handleConfirmReset = async (event) => {
    event.preventDefault();

    if (!resetCode.trim()) {
      setError("Enter the verification code sent to your email.");
      return;
    }

    if (!resetPasswordsMatch) {
      setError("The new passwords do not match.");
      return;
    }

    setError("");
    setAuthActionLoading(true);

    try {
      await confirmResetPassword({
        username: normalizeUsername(),
        confirmationCode: resetCode.trim(),
        newPassword: resetPasswordValue,
      });

      setPassword("");
      setResetCode("");
      setResetPasswordValue("");
      setConfirmResetPasswordValue("");
      setLoginMode(MODES.RESET_SUCCESS);
    } catch (resetError) {
      console.error("Password-reset confirmation error:", resetError);

      switch (resetError?.name) {
        case "CodeMismatchException":
          setError("The verification code is incorrect.");
          break;
        case "ExpiredCodeException":
          setError("The verification code has expired. Request a new code.");
          break;
        case "InvalidPasswordException":
          setError("The password does not meet the security requirements.");
          break;
        default:
          setError("We could not reset the password. Check the code and try again.");
      }
    } finally {
      setAuthActionLoading(false);
    }
  };

  const resendResetCode = async () => {
    setError("");
    setAuthActionLoading(true);

    try {
      await resetPassword({ username: normalizeUsername() });
      toast.success("A new verification code has been requested.");
    } catch (resetError) {
      console.error("Reset-code resend error:", resetError);
      toast.info("If the account is eligible, a new code will be sent.");
    } finally {
      setAuthActionLoading(false);
    }
  };

  const returnToSignIn = () => {
    setError("");
    setLoginMode(MODES.SIGN_IN);
    setResetCode("");
    setResetPasswordValue("");
    setConfirmResetPasswordValue("");
    setResetDeliveryDestination("");
  };

const renderErrorMessage = () =>
  error ? (
    <div className="login-error-message" role="alert">
      {error}
    </div>
  ) : null;

  const renderSignInForm = () => (
    <>
      <div className="login-form-header">
        <span className="login-form-kicker">Client Portal</span>
        <h2>{showSetNewPassword ? "Create a New Password" : "Welcome Back"}</h2>
        <p>
          {showSetNewPassword
            ? "Create a secure password to finish activating your account."
            : "Enter your credentials to continue."}
        </p>
      </div>

      <form className="loginform" onSubmit={handleSignIn}>
        <div className="inputs">
          <label className="login-field">
            <span className="login-field-label">Email address</span>
            <div className={`login-input-wrapper ${error ? "input-error" : ""}`}>
              <PortraitIcon className="login-input-icon" aria-hidden="true" />
              <input
                type="email"
                value={userName}
                placeholder="name@company.com"
                onChange={(event) => setUserName(event.target.value)}
                autoComplete="username"
                required
                disabled={showSetNewPassword || authActionLoading}
              />
            </div>
          </label>

          {!showSetNewPassword && (
            <label className="login-field">
              <span className="login-field-label">Password</span>
              <div className={`login-input-wrapper ${error ? "input-error" : ""}`}>
                <KeyIcon className="login-input-icon" aria-hidden="true" />
                <input
                  type="password"
                  value={passWord}
                  placeholder="Enter your password"
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                  disabled={authActionLoading}
                />
              </div>
            </label>
          )}

          {showSetNewPassword && (
            <div className="new-password-fields">
              <label className="login-field">
                <span className="login-field-label">New password</span>
                <div className="login-input-wrapper">
                  <KeyIcon className="login-input-icon" aria-hidden="true" />
                  <input
                    type="password"
                    value={newPasswordString}
                    placeholder="Create a new password"
                    onChange={(event) => setNewPasswordString(event.target.value)}
                    autoComplete="new-password"
                    required
                    autoFocus
                  />
                </div>
              </label>

              <label className="login-field">
                <span className="login-field-label">Confirm new password</span>
                <div className="login-input-wrapper">
                  <KeyIcon className="login-input-icon" aria-hidden="true" />
                  <input
                    type="password"
                    value={confirmNewPasswordString}
                    placeholder="Confirm your new password"
                    onChange={(event) => setConfirmNewPasswordString(event.target.value)}
                    autoComplete="new-password"
                    required
                  />
                  {passwordsMatch && (
                    <CheckCircleIcon className="password-match-icon" aria-label="Passwords match" />
                  )}
                </div>
              </label>
            </div>
          )}

          {renderErrorMessage()}
        </div>

        <button
          className="login-submit-button"
          type="submit"
          disabled={
            authActionLoading ||
            (showSetNewPassword
              ? !passwordsMatch
              : !normalizeUsername() || !passWord.trim())
          }
        >
          {authActionLoading
            ? showSetNewPassword
              ? "Setting Password..."
              : "Signing In..."
            : showSetNewPassword
              ? "Set New Password"
              : "Sign In"}
        </button>
      </form>

      {!showSetNewPassword && (
        <button
          type="button"
          className="forgot-password-button"
          onClick={() => {
            setError("");
            setLoginMode(MODES.REQUEST_RESET);
          }}
        >
          Forgot your password?
        </button>
      )}
    </>
  );

  const renderRequestResetForm = () => (
    <>
      <div className="login-form-header">
        <span className="login-form-kicker">Account Recovery</span>
        <h2>Reset Your Password</h2>
        <p>
          Enter the email address associated with your account. We will send a
          verification code to the verified recovery address.
        </p>
      </div>

      <form className="loginform" onSubmit={handleRequestReset}>
        <div className="inputs">
          <label className="login-field">
            <span className="login-field-label">Email address</span>
            <div className={`login-input-wrapper ${error ? "input-error" : ""}`}>
              <PortraitIcon className="login-input-icon" aria-hidden="true" />
              <input
                type="email"
                value={userName}
                placeholder="name@company.com"
                onChange={(event) => setUserName(event.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
            </div>
          </label>
          {renderErrorMessage()}
        </div>

        <button
          className="login-submit-button"
          type="submit"
          disabled={authActionLoading || !normalizeUsername()}
        >
          {authActionLoading ? "Sending Code..." : "Send Verification Code"}
        </button>
      </form>

      <button type="button" className="forgot-password-button" onClick={returnToSignIn}>
        Return to sign in
      </button>
    </>
  );

  const renderConfirmResetForm = () => (
    <>
      <div className="login-form-header">
        <span className="login-form-kicker">Verify Your Identity</span>
        <h2>Check Your Email</h2>
        <p>
          If an eligible account exists, a verification code was sent to{" "}
          <strong>{resetDeliveryDestination}</strong>.
        </p>
      </div>

      <form className="loginform" onSubmit={handleConfirmReset}>
        <div className="inputs">
          <label className="login-field">
            <span className="login-field-label">Verification code</span>
            <div className="login-input-wrapper">
              <MarkEmailReadOutlinedIcon className="login-input-icon" aria-hidden="true" />
              <input
                type="text"
                value={resetCode}
                placeholder="Enter the verification code"
                onChange={(event) => setResetCode(event.target.value.replace(/\s/g, ""))}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                autoFocus
              />
            </div>
          </label>

          <label className="login-field">
            <span className="login-field-label">New password</span>
            <div className="login-input-wrapper">
              <KeyIcon className="login-input-icon" aria-hidden="true" />
              <input
                type="password"
                value={resetPasswordValue}
                placeholder="Create a new password"
                onChange={(event) => setResetPasswordValue(event.target.value)}
                autoComplete="new-password"
                required
              />
            </div>
          </label>

          <label className="login-field">
            <span className="login-field-label">Confirm new password</span>
            <div className="login-input-wrapper">
              <KeyIcon className="login-input-icon" aria-hidden="true" />
              <input
                type="password"
                value={confirmResetPasswordValue}
                placeholder="Confirm your new password"
                onChange={(event) => setConfirmResetPasswordValue(event.target.value)}
                autoComplete="new-password"
                required
              />
              {resetPasswordsMatch && (
                <CheckCircleIcon className="password-match-icon" aria-label="Passwords match" />
              )}
            </div>
          </label>

          <div className="password-requirements">
            Use a strong password that meets your organization&apos;s password requirements.
          </div>
          {renderErrorMessage()}
        </div>

        <button
          className="login-submit-button"
          type="submit"
          disabled={authActionLoading || !resetCode.trim() || !resetPasswordsMatch}
        >
          {authActionLoading ? "Updating Password..." : "Set New Password"}
        </button>
      </form>

      <div className="reset-secondary-actions">
        <button
          type="button"
          className="forgot-password-button"
          onClick={resendResetCode}
          disabled={authActionLoading}
        >
          Resend verification code
        </button>
        <button
          type="button"
          className="forgot-password-button"
          onClick={() => {
            setError("");
            setLoginMode(MODES.REQUEST_RESET);
          }}
        >
          Use a different email
        </button>
      </div>
    </>
  );

  const renderResetSuccess = () => (
    <div className="reset-success-panel">
      <CheckCircleIcon className="reset-success-icon" aria-hidden="true" />
      <span className="login-form-kicker">Password Updated</span>
      <h2>Your password has been reset</h2>
      <p>You can now sign in using your new password.</p>
      <button type="button" className="login-submit-button" onClick={returnToSignIn}>
        Return to Sign In
      </button>
    </div>
  );

const renderForm = () => {
  if (loginMode === MODES.REQUEST_RESET) {
    return renderRequestResetForm();
  }

  if (loginMode === MODES.CONFIRM_RESET) {
    return renderConfirmResetForm();
  }

  if (loginMode === MODES.RESET_SUCCESS) {
    return renderResetSuccess();
  }

  return renderSignInForm();
};

  return (
    <div className="login_page">
      {loadingSequence ? (
        <div className="login-loading-screen">
          <div className="login-loading-logo">
            <img src="/weathervaneLogo.svg" alt="Weathervane Intel" />
          </div>
          <div className="loading-text" aria-live="polite">
            {currentPhrase}
          </div>
        </div>
      ) : (
        <main className="login-shell">
          <ToastContainer position="top-center" closeOnClick theme="dark" />

          <section className="login-panel">
            <div className="login-brand-panel">
              <button
                type="button"
                className="login-logo-button"
                onClick={() => {
                  window.location.href = "https://weathervaneintel.com";
                }}
                aria-label="Visit the Weathervane Intel website"
              >
                <img
                  src="/weathervaneLogo.svg"
                  alt="Weathervane Intel"
                  className="login-logo"
                />
              </button>

              <div className="login-brand-copy">
                <span className="login-eyebrow">Weathervane Intel</span>
                <h1>Secure Client Access</h1>
                <p>
                  Access the Weathervane intelligence platform and your authorized
                  protective intelligence resources.
                </p>
              </div>

              <div className="login-security-note">
                <span className="login-security-indicator" />
                <p>
                  Authorized users only. Access and activity may be logged for
                  security purposes.
                </p>
              </div>
            </div>

            <div className="login-form-panel">
              {renderForm()}
              <div className="login-support">
                <p>
                  Need assistance?{" "}
                  <a href="mailto:info@weathervaneintel.com">Contact support</a>
                </p>
              </div>
            </div>
          </section>
        </main>
      )}
    </div>
  );
};
